import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'
import type { DigitalProduct, DigitalProductFile } from './digitalProduct.service'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        const base = `${config.apiUrl}`
        apiInstance = axios.create({
            baseURL: base,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

export interface MyFilesPurchasedProduct {
    id: string
    title: string
    slug: string
    description: string
    thumbnail_url?: string
    file_count: number
    purchased_at: string
}

export interface MyFilesData {
    products: MyFilesPurchasedProduct[]
    total_products: number
    total_files: number
}

export interface ProductFilesData {
    product: DigitalProduct
    files: DigitalProductFile[]
    total_files: number
    total_size_bytes: number
}

export interface ProductAccessCheck {
    has_access: boolean
}

export const myFilesService = {
    getPurchasedProducts,
    getProductFiles,
    getFileCategories,
    trackDownload,
    checkProductAccess,
}

/**
 * Get all digital products purchased by the current user
 */
async function getPurchasedProducts(): Promise<ApiResponse<MyFilesData>> {
    try {
        const response = await getApiInstance().get('/my-files/')

        if (response.data?.success && response.data?.data) {
            return {
                success: true,
                data: response.data.data
            }
        }

        return {
            success: false,
            error: 'Unexpected response format'
        }
    } catch (error: any) {
        console.error('Error fetching purchased products:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch your files'
        }
    }
}

/**
 * Get files for a purchased digital product
 */
async function getProductFiles(
    productSlug: string,
    category?: string
): Promise<ApiResponse<ProductFilesData>> {
    try {
        const params: Record<string, string> = {}
        if (category) {
            params.category = category
        }

        const response = await getApiInstance().get(`/my-files/${productSlug}`, { params })

        if (response.data?.success && response.data?.data) {
            return {
                success: true,
                data: response.data.data
            }
        }

        return {
            success: false,
            error: 'Unexpected response format'
        }
    } catch (error: any) {
        console.error('Error fetching product files:', error)

        // Handle 403 Forbidden (not purchased)
        if (error.response?.status === 403) {
            return {
                success: false,
                error: "You don't have access to this product"
            }
        }

        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch product files'
        }
    }
}

/**
 * Get available file categories for a product
 */
async function getFileCategories(productSlug: string): Promise<ApiResponse<{ categories: string[] }>> {
    try {
        const response = await getApiInstance().get(`/my-files/${productSlug}/categories`)

        if (response.data?.success && response.data?.data) {
            return {
                success: true,
                data: response.data.data
            }
        }

        return {
            success: true,
            data: { categories: [] }
        }
    } catch (error: any) {
        console.error('Error fetching categories:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch categories'
        }
    }
}

/**
 * Track file download and get download URL
 */
async function trackDownload(
    productSlug: string,
    fileId: string
): Promise<ApiResponse<{ download_url: string }>> {
    try {
        const response = await getApiInstance().post(`/my-files/${productSlug}/files/${fileId}/download`)

        if (response.data?.success && response.data?.data) {
            return {
                success: true,
                data: response.data.data
            }
        }

        return {
            success: false,
            error: 'Failed to get download URL'
        }
    } catch (error: any) {
        console.error('Error tracking download:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to track download'
        }
    }
}

/**
 * Check if user has access to a digital product
 */
async function checkProductAccess(productSlug: string): Promise<ApiResponse<ProductAccessCheck>> {
    try {
        const response = await getApiInstance().get(`/my-files/check-access/${productSlug}`)

        if (response.data?.success && response.data?.data) {
            return {
                success: true,
                data: response.data.data
            }
        }

        return {
            success: true,
            data: { has_access: false }
        }
    } catch (error: any) {
        console.error('Error checking product access:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to check access'
        }
    }
}
